import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Trash2 } from 'lucide-react';
import { useModalDrag } from '../../hooks/useModalDrag';
import { useAdrForm } from '../useAdrForm';
import { useAdrActions } from '../../stores';
import { generateAdrId, getAdrById } from '../adrStore';
import { AdrFormTabs } from './AdrFormTabs';
import type { ADR } from '../../types';

// ============================================
// Types
// ============================================

export interface AdrModalProps {
  isOpen: boolean;
  onClose: () => void;
  adrId?: string; // undefined = create new
  defaultScope?: ADR['scope'];
}

// ============================================
// AdrModal
// ============================================

export function AdrModal({ isOpen, onClose, adrId, defaultScope }: AdrModalProps) {
  const { t } = useTranslation();
  const actions = useAdrActions();
  const { modalRef, position, isDragging, handleMouseDown, handleDoubleClick } = useModalDrag();

  // For new ADRs: create draft in store on open
  const [draftId, setDraftId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && !adrId && !draftId) {
      const id = generateAdrId('');
      const now = new Date().toISOString();
      actions.addAdr({
        id,
        title: '',
        scope: defaultScope ?? { type: 'global' },
        context: [],
        decision: [],
        consequences: [],
        status: 'draft',
        implementationStatus: 'pending',
        relations: [],
        people: [],
        comments: [],
        createdAt: now,
        updatedAt: now,
      });
      setDraftId(id);
    }
  }, [isOpen, adrId, draftId, actions, defaultScope]);

  // Clean up draftId when modal closes
  useEffect(() => {
    if (!isOpen) setDraftId(null);
  }, [isOpen]);

  const effectiveId = adrId || draftId || undefined;
  const form = useAdrForm(effectiveId);

  const handleClose = useCallback(() => {
    // Flush any pending editor content
    form.flushEditors();
    // Delete draft if title is empty — read from store directly (never stale)
    if (draftId) {
      const storeAdr = getAdrById(draftId);
      if (!storeAdr?.title?.trim()) {
        actions.deleteAdr(draftId);
      }
    }
    setDraftId(null);
    onClose();
  }, [form.flushEditors, draftId, actions, onClose]);

  const handleDelete = useCallback(() => {
    if (form.deleteAdr()) {
      setDraftId(null);
      onClose();
    }
  }, [form, onClose]);

  // Keyboard
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        handleClose();
        return;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  if (!isOpen) return null;
  // Wait for draft creation
  if (!effectiveId) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[10001]" onClick={handleClose} />
      <div className="fixed inset-0 z-[10002] flex items-center justify-center p-4 pointer-events-none">
        <div
          ref={modalRef}
          className={`bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col ${position ? '' : 'pointer-events-auto'}`}
          style={position ? { position: 'fixed', left: position.x, top: position.y, pointerEvents: 'auto' } : undefined}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-2 border-b border-gray-100 shrink-0"
            onMouseDown={handleMouseDown}
            onDoubleClick={handleDoubleClick}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <span className="text-xs font-medium text-gray-500">
              {adrId ? t('adr.editAdr') : t('adr.newAdr')}
            </span>
            <button onClick={handleClose} onMouseDown={e => e.stopPropagation()} className="text-gray-400 hover:text-gray-600">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <AdrFormTabs form={form} px="px-4" autoFocusTitle />

          {/* Footer */}
          <div className="px-4 py-2 border-t border-gray-100 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              {adrId && (
                <button
                  onClick={handleDelete}
                  className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                >
                  <Trash2 className="w-3 h-3" />
                  {t('common.delete')}
                </button>
              )}
            </div>
            <span className="text-[10px] text-gray-400">ESC · Cmd+Enter</span>
          </div>
        </div>
      </div>
    </>
  );
}
